/**
 * Level Dependency Detection
 *
 * Detects when editing a level will affect later levels,
 * enabling smart warnings and cascading updates.
 */

/**
 * Detect which later levels are affected by changes to a given level
 *
 * @param {number} editingLevel - The level being edited
 * @param {Object} oldChoices - Previous choices at this level
 * @param {Object} newChoices - New choices being made
 * @param {Object} character - Full character state
 * @returns {Object} - Affected levels and reasons
 */
export function detectDependencies(editingLevel, oldChoices, newChoices, character) {
  const affectedLevels = {};
  const maxLevel = character.level || 1;

  // Only check levels after the one being edited
  for (let level = editingLevel + 1; level <= maxLevel; level++) {
    const reasons = [];

    // Check trait marking dependencies
    const traitChanges = detectTraitMarkingChanges(editingLevel, oldChoices, newChoices, level, character);
    if (traitChanges.length > 0) {
      reasons.push(...traitChanges);
    }

    // Check class/subclass dependencies
    const classChanges = detectClassChanges(editingLevel, oldChoices, newChoices, level, character);
    if (classChanges.length > 0) {
      reasons.push(...classChanges);
    }

    // Check domain card dependencies
    const domainCardChanges = detectDomainCardChanges(editingLevel, oldChoices, newChoices, level, character);
    if (domainCardChanges.length > 0) {
      reasons.push(...domainCardChanges);
    }

    // Check multiclass dependencies
    const multiclassChanges = detectMulticlassChanges(editingLevel, oldChoices, newChoices, level, character);
    if (multiclassChanges.length > 0) {
      reasons.push(...multiclassChanges);
    }

    if (reasons.length > 0) {
      affectedLevels[level] = reasons;
    }
  }

  return affectedLevels;
}

/**
 * Detect trait marking conflicts
 */
function detectTraitMarkingChanges(editingLevel, oldChoices, newChoices, checkLevel, character) {
  const reasons = [];

  // Get tier info
  const editingTier = editingLevel <= 1 ? 1 : editingLevel <= 4 ? 2 : editingLevel <= 7 ? 3 : 4;
  const checkTier = checkLevel <= 1 ? 1 : checkLevel <= 4 ? 2 : checkLevel <= 7 ? 3 : 4;

  // Only check if in same tier (trait marks clear at tier boundaries)
  if (editingTier === checkTier) {
    const oldMarkedTraits = oldChoices?.selectedTraits || [];
    const newMarkedTraits = newChoices?.selectedTraits || [];

    // Check if later level tries to mark a trait that's now marked earlier
    const levelHistory = character.levelHistory?.[checkLevel];
    const laterMarkedTraits = levelHistory?.choices?.selectedTraits || [];

    const newlyMarked = newMarkedTraits.filter(t => !oldMarkedTraits.includes(t));
    const conflicts = laterMarkedTraits.filter(t => newlyMarked.includes(t));

    if (conflicts.length > 0) {
      reasons.push({
        type: 'trait_marking',
        severity: 'high',
        message: `Level ${checkLevel} marked ${conflicts.join(', ')} but you're now marking them at Level ${editingLevel}`,
        requiresReset: true
      });
    }
  }

  return reasons;
}

/**
 * Detect class/subclass change impacts
 */
function detectClassChanges(editingLevel, oldChoices, newChoices, checkLevel, character) {
  const reasons = [];

  // Only Level 1 has class selection
  if (editingLevel !== 1) {
    return reasons;
  }

  const oldClass = oldChoices?.class;
  const newClass = newChoices?.class;
  const oldSubclass = oldChoices?.subclass;
  const newSubclass = newChoices?.subclass;

  if (oldClass !== newClass) {
    reasons.push({
      type: 'class_change',
      severity: 'critical',
      message: `Changing class affects domain cards and all class features`,
      requiresReset: true
    });
  } else if (oldSubclass !== newSubclass) {
    reasons.push({
      type: 'subclass_change',
      severity: 'high',
      message: `Changing subclass affects all subclass features and upgrades`,
      requiresReset: true
    });
  }

  return reasons;
}

/**
 * Detect domain card selection impacts
 */
function detectDomainCardChanges(editingLevel, oldChoices, newChoices, checkLevel, character) {
  const reasons = [];

  const oldCards = oldChoices?.loadout || oldChoices?.selectedDomainCards || [];
  const newCards = newChoices?.loadout || newChoices?.selectedDomainCards || [];

  // Cards removed from loadout
  const removedCards = oldCards.filter(c => !newCards.includes(c));

  // Cards added to loadout
  const addedCards = newCards.filter(c => !oldCards.includes(c));

  if (removedCards.length > 0 || addedCards.length > 0) {
    reasons.push({
      type: 'domain_cards',
      severity: 'medium',
      message: `Domain card loadout changed - may affect card availability at later levels`,
      requiresReset: false,
      details: {
        removed: removedCards,
        added: addedCards
      }
    });
  }

  return reasons;
}

/**
 * Detect multiclass impacts
 */
function detectMulticlassChanges(editingLevel, oldChoices, newChoices, checkLevel, character) {
  const reasons = [];

  const oldMulticlass = oldChoices?.advancements?.includes('multiclass');
  const newMulticlass = newChoices?.advancements?.includes('multiclass');

  if (oldMulticlass && !newMulticlass) {
    reasons.push({
      type: 'multiclass_removed',
      severity: 'critical',
      message: `Removing multiclass affects domain card restrictions at all later levels`,
      requiresReset: true
    });
  } else if (!oldMulticlass && newMulticlass) {
    reasons.push({
      type: 'multiclass_added',
      severity: 'high',
      message: `Adding multiclass restricts multiclass domain cards to half level`,
      requiresReset: false
    });
  }

  return reasons;
}

/**
 * Calculate which levels need to be reset based on dependencies
 *
 * @param {Object} affectedLevels - Output from detectDependencies
 * @returns {Array} - Array of level numbers that must be reset
 */
export function getLevelsToReset(affectedLevels) {
  const levelsToReset = [];

  Object.entries(affectedLevels).forEach(([level, reasons]) => {
    const hasHighSeverity = reasons.some(r => r.requiresReset);
    if (hasHighSeverity) {
      levelsToReset.push(parseInt(level));
    }
  });

  // If any level needs reset, all levels after it also need reset
  if (levelsToReset.length > 0) {
    const minLevel = Math.min(...levelsToReset);
    const allLevels = Object.keys(affectedLevels).map(l => parseInt(l));
    return allLevels.filter(l => l >= minLevel);
  }

  return [];
}

/**
 * Generate human-readable summary of dependencies
 *
 * @param {Object} affectedLevels - Output from detectDependencies
 * @returns {string} - Summary message
 */
export function generateDependencySummary(affectedLevels) {
  const levelsCount = Object.keys(affectedLevels).length;

  if (levelsCount === 0) {
    return 'No later levels will be affected by this change.';
  }

  const levels = Object.keys(affectedLevels).map(l => `Level ${l}`).join(', ');
  const hasResets = Object.values(affectedLevels).some(reasons =>
    reasons.some(r => r.requiresReset)
  );

  if (hasResets) {
    return `⚠️ This change will reset ${levels}. You'll need to re-make those advancement choices.`;
  } else {
    return `ℹ️ This change may affect ${levels}. Review those levels after saving.`;
  }
}
